import React, { useEffect, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Check, X, ShieldAlert, Eye } from 'lucide-react';

const STATUS_OPTIONS = [
    { v: '', l: 'All' },
    { v: 'pending_confirmation', l: 'Awaiting customer' },
    { v: 'pending_approval', l: 'Pending approval' },
    { v: 'approved', l: 'Approved' },
    { v: 'rejected', l: 'Rejected' },
    { v: 'fraud_review', l: 'Fraud review' },
];

export default function AdminBills() {
    const [bills, setBills] = useState([]);
    const [status, setStatus] = useState('pending_approval');
    const [selected, setSelected] = useState(null);
    const [details, setDetails] = useState(null);

    const load = () => {
        const q = status ? `?status=${status}` : '';
        api.get(`/admin/bills${q}`).then(({ data }) => setBills(data));
    };
    useEffect(load, [status]);

    const view = async (id) => {
        const { data } = await api.get(`/admin/bills/${id}`);
        setDetails(data); setSelected(id);
    };

    const act = async (id, action, reason) => {
        try {
            await api.post(`/admin/bills/${id}/review`, { action, reason });
            toast.success(`Bill ${action}d`);
            setSelected(null); setDetails(null); load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Action failed');
        }
    };

    return (
        <AdminLayout>
            <div className="p-10" data-testid="admin-bills-page">
                <div className="flex items-end justify-between mb-8">
                    <div>
                        <div className="overline">Bill Approvals</div>
                        <h1 className="font-serif text-4xl text-navy mt-2">Review Queue</h1>
                    </div>
                    <select value={status} onChange={(e) => setStatus(e.target.value)} className="input-box max-w-[240px]" data-testid="bills-status-filter">
                        {STATUS_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                    </select>
                </div>

                <div className="bg-white border border-border-subtle">
                    <table className="w-full text-sm" data-testid="bills-table">
                        <thead className="bg-cream text-text-tertiary text-xs uppercase tracking-widest">
                            <tr><th className="text-left p-4">Customer</th><th className="text-left p-4">Mall / Brand</th><th className="text-left p-4">Amount</th><th className="text-left p-4">Points</th><th className="text-left p-4">Confidence</th><th className="text-left p-4">Status</th><th className="text-right p-4">Action</th></tr>
                        </thead>
                        <tbody>
                            {bills.map((b) => (
                                <tr key={b.id} className="border-t border-border-subtle hover:bg-cream/50">
                                    <td className="p-4"><div className="text-navy">{b.customer_name}</div><div className="text-xs text-text-tertiary">{b.customer_email}</div></td>
                                    <td className="p-4"><div className="text-navy">{b.brand_name || '—'}</div><div className="text-xs text-text-tertiary">{b.mall_name}</div></td>
                                    <td className="p-4">₹{(b.total_amount || 0).toLocaleString('en-IN')}</td>
                                    <td className="p-4 font-mono text-gold">+{b.points_to_credit}</td>
                                    <td className="p-4">{b.ocr_confidence?.toFixed(0)}%</td>
                                    <td className="p-4 capitalize text-xs">{b.status.replace(/_/g, ' ')}</td>
                                    <td className="p-4 text-right">
                                        <button onClick={() => view(b.id)} className="btn-link" data-testid={`view-bill-${b.id}`}><Eye size={14} className="inline" /> View</button>
                                    </td>
                                </tr>
                            ))}
                            {bills.length === 0 && <tr><td colSpan={7} className="p-12 text-center text-text-tertiary">No bills in this queue.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>

            {selected && details && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6" onClick={() => { setSelected(null); setDetails(null); }} data-testid="bill-detail-modal">
                    <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="p-8 border-b border-border-subtle flex items-center justify-between">
                            <div>
                                <div className="overline">Bill Review</div>
                                <div className="font-serif text-2xl text-navy mt-1">{details.brand_name || details.mall_name}</div>
                            </div>
                            <button onClick={() => setSelected(null)} className="text-text-tertiary hover:text-navy" data-testid="close-bill-modal"><X size={24} /></button>
                        </div>
                        <div className="p-8 grid md:grid-cols-2 gap-6">
                            <div>
                                <div className="overline mb-2">Bill Image</div>
                                <div className="bg-cream aspect-[3/4] overflow-hidden">
                                    <img src={details.image_data_url} alt="bill" className="w-full h-full object-contain" />
                                </div>
                            </div>
                            <div className="space-y-3 text-sm">
                                <Row label="Customer" value={`${details.customer_name} (${details.customer_email})`} />
                                <Row label="Mall" value={details.mall_name} />
                                <Row label="Brand" value={details.brand_name || '—'} />
                                <Row label="Invoice #" value={details.invoice_number || '—'} />
                                <Row label="Invoice Date" value={details.invoice_date || '—'} />
                                <Row label="Amount" value={`₹${(details.total_amount || 0).toLocaleString('en-IN')}`} />
                                <Row label="GST" value={details.gst_number || '—'} />
                                <Row label="OCR Confidence" value={`${(details.ocr_confidence || 0).toFixed(0)}%`} />
                                <Row label="Points" value={`+${details.points_to_credit}`} />
                                <Row label="Status" value={details.status} />
                                {details.fraud_flags?.length > 0 && (
                                    <div className="bg-amber-50 border border-amber-200 p-3 text-amber-800 text-xs">
                                        <ShieldAlert size={14} className="inline mr-1" /> Flags: {details.fraud_flags.join(', ')}
                                    </div>
                                )}
                                <div className="pt-6 flex gap-3">
                                    <button onClick={() => act(details.id, 'approve')} className="btn-primary" data-testid="approve-bill-btn"><Check size={14} /> Approve</button>
                                    <button onClick={() => { const r = prompt('Reason for rejection?'); if (r) act(details.id, 'reject', r); }} className="btn-outline" data-testid="reject-bill-btn"><X size={14} /> Reject</button>
                                    <button onClick={() => act(details.id, 'fraud', 'Suspected fraud')} className="btn-outline border-rose-600 text-rose-600 hover:bg-rose-600 hover:text-white" data-testid="fraud-bill-btn">Mark Fraud</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex justify-between gap-4 py-2 border-b border-border-subtle last:border-0">
            <span className="text-text-tertiary uppercase tracking-widest text-xs">{label}</span>
            <span className="text-navy text-right">{value}</span>
        </div>
    );
}
